/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';

import {
    DividerNew as Divider,
    FlexRow,
    FlexCol,
    formatMoney,
    Stack,
    Text,
    PlaceholderText,
    useFlags,
    VisualOnlyLabel,
} from 'sarsaparilla';
import SitePassSummary from './SitePassSummary';

function DetailSummary({
    isExistingReservation,
    showSitePasses,
    date,
    endDate,
    startTime,
    endTime,
    validDays,
}) {
    const { ticketAdjustMultiDayTicket } = useFlags();
    const allowMultiDays = useMemo(() => {
        return ticketAdjustMultiDayTicket && !!validDays && validDays > 1;
    }, [ticketAdjustMultiDayTicket, validDays]);
    const orderDetails = useSelector((state) => state?.timedEntry?.orderDetails);
    const sitePassTotalPrice = useSelector(
        (state) => state?.timedEntry?.orderDetails?.sitePass?.totalPrice
    );
    const { price_summary: priceSummary = {} } = orderDetails;
    const { previous_payments: previousPayments, total_cost: totalCost } = priceSummary;
    const newTotalCost = totalCost + sitePassTotalPrice;
    const amountDue = totalCost - previousPayments;

    const getTotalDescription = () => {
        if (previousPayments <= 0) {
            return 'Total Cost';
        }
        if (amountDue < 0) {
            return 'Refund Due';
        }
        return 'Amount Due';
    };

    const renderReservationDateTime = () => {
        return (
            <Stack space="xs">
                <FlexRow>
                    {!allowMultiDays && (
                        <>
                            <FlexCol md={6} xs={6}>
                                <VisualOnlyLabel isAriaHidden={false}>
                                    Date
                                </VisualOnlyLabel>
                                <Text>{date}</Text>
                            </FlexCol>
                            <FlexCol md={6} xs={6}>
                                <VisualOnlyLabel isAriaHidden={false}>
                                    Start Time
                                </VisualOnlyLabel>
                                <Text>{startTime}</Text>
                            </FlexCol>
                        </>
                    )}

                    {!!allowMultiDays && (
                        <>
                            <FlexCol md={6} xs={6}>
                                <VisualOnlyLabel isAriaHidden={false}>
                                    Dates
                                </VisualOnlyLabel>
                                <Text>{`${date} - ${endDate}`}</Text>
                            </FlexCol>
                            <FlexCol md={6} xs={6}>
                                <VisualOnlyLabel isAriaHidden={false}>
                                    Entry Window
                                </VisualOnlyLabel>
                                <Text>{`${startTime} - ${endTime}`}</Text>
                            </FlexCol>
                        </>
                    )}
                </FlexRow>
            </Stack>
        );
    };

    const renderSummaryItems = () => {
        const pendingLineItems =
            orderDetails?.reservation?.pending_modifications?.current_line_items;
        const lineItems = pendingLineItems?.length
            ? pendingLineItems
            : priceSummary.summary_items;

        return (
            <Stack space="xs">
                {lineItems &&
                    lineItems
                        //Filters out Ticketing CLIN - Internet Reservation (fee_type=0)
                        .filter((item) => item.fee_type !== 0)
                        .sort((a, b) => (a.displayOrder < b.displayOrder ? -1 : 1))
                        .map(({ description, quantity, unit_price: unitPrice }) => {
                            return (
                                <FlexRow
                                    key={`order-summary-item-${description}-${unitPrice}`}
                                >
                                    <FlexCol xs={8}>
                                        <Text size="sm">
                                            {description} x {quantity}
                                        </Text>
                                    </FlexCol>
                                    <FlexCol className="rec-price-value">
                                        <Text size="sm" style={{ textAlign: 'right' }}>
                                            {formatMoney(unitPrice * quantity)}
                                        </Text>
                                    </FlexCol>
                                </FlexRow>
                            );
                        })}
            </Stack>
        );
    };

    const renderTotalCost = () => {
        if (totalCost !== 0 && previousPayments !== 0) {
            return (
                <>
                    <Divider />
                    <FlexRow>
                        <Divider />
                        <FlexCol>
                            <Text fontWeight="semibold">
                                {previousPayments <= 0 ? 'Total Cost' : 'Amount Due'}
                            </Text>
                        </FlexCol>
                        <FlexCol>
                            <Text fontWeight="semibold" style={{ textAlign: 'right' }}>
                                {formatMoney(amountDue)}
                            </Text>
                        </FlexCol>
                    </FlexRow>
                </>
            );
        }

        return (
            <>
                <Divider />
                <FlexRow>
                    <FlexCol xs={8} className="rec-price-label">
                        <Text size="sm">Total</Text>
                    </FlexCol>
                    <FlexCol>
                        <Text size="sm" style={{ textAlign: 'right' }}>
                            {formatMoney(newTotalCost)}
                        </Text>
                    </FlexCol>
                </FlexRow>
            </>
        );
    };

    const renderPreviousPayments = () => {
        return (
            previousPayments > 0 && (
                <FlexRow className="rec-price-subtotal">
                    <FlexCol xs={8}>
                        <Text size="sm">Previous Payments</Text>
                    </FlexCol>
                    <FlexCol className="rec-price-value">
                        <Text size="sm" style={{ textAlign: 'right' }}>
                            {formatMoney(previousPayments)}
                        </Text>
                    </FlexCol>
                </FlexRow>
            )
        );
    };

    const renderBalance = () => {
        if (isExistingReservation) {
            return (
                <>
                    <Divider />
                    <FlexRow>
                        <FlexCol className="rec-price-label">
                            <Text fontWeight="semibold">{getTotalDescription()}</Text>
                        </FlexCol>
                        <FlexCol className="rec-price-value">
                            <Text fontWeight="semibold" style={{ textAlign: 'right' }}>
                                {formatMoney(amountDue)}
                            </Text>
                        </FlexCol>
                    </FlexRow>
                </>
            );
        }

        return null;
    };

    if (priceSummary?.loading) {
        return <PlaceholderText fontSize={11} lines={3} />;
    }

    return (
        <>
            {renderReservationDateTime()}
            {renderSummaryItems()}
            <SitePassSummary showSitePasses={showSitePasses} />
            <Stack space="xs">
                {renderTotalCost()}
                {renderPreviousPayments()}
            </Stack>
            {renderBalance()}
        </>
    );
}

DetailSummary.propTypes = {
    isExistingReservation: PropTypes.bool,
    showSitePasses: PropTypes.bool,
    date: PropTypes.string,
    endDate: PropTypes.string,
    startTime: PropTypes.string,
    endTime: PropTypes.string,
    validDays: PropTypes.number,
};

DetailSummary.defaultProps = {
    isExistingReservation: false,
    showSitePasses: false,
    validDays: 0,
};

export default DetailSummary;
